import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AirlineDetail,
  type AirlineListItem,
  type CreateAirlineRequest,
  CreateAirlineRequestSchema,
  type UpdateAirlineRequest,
  UpdateAirlineRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useCountryOptions, useCreateAirline, useUpdateAirline } from '../use-airlines';

interface AirlineFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  airline?: AirlineDetail | AirlineListItem | null;
}

const CREATE_DEFAULTS: CreateAirlineRequest = {
  iataCode: '',
  name: '',
  isLowCost: false,
  isActive: true,
  sortOrder: 0,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 *
 * IATA and ICAO codes are force-uppercased on input; the shared Zod schema
 * also normalizes them on the server, but uppercasing here gives operators
 * immediate visual feedback.
 */
const AirlineFields = ({
  pending,
  countrySelectOptions,
  countriesLoading,
}: {
  pending: boolean;
  countrySelectOptions: ReadonlyArray<{ value: string; label: string }>;
  countriesLoading: boolean;
}): React.JSX.Element => (
  <>
    <UzFormField name="iataCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
          label="IATA Code"
          placeholder="2-3 letters/digits, e.g. AI"
          maxLength={3}
          hint="The airline's IATA designator. Must be unique."
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="icaoCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          // Empty maps to null so the optional ICAO field validates when blank
          // (the schema rejects '' against the 3-letter rule).
          onChange={(e) =>
            field.onChange(e.target.value === '' ? null : e.target.value.toUpperCase())
          }
          label="ICAO Code"
          placeholder="3 letters, e.g. AIC"
          maxLength={3}
          hint="Optional. Must be unique when provided."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Air India"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="countryId">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Country"
          placeholder={countriesLoading ? 'Loading countries…' : 'Select a country'}
          options={countrySelectOptions}
          value={(field.value as string | null | undefined) || undefined}
          onValueChange={(v) => field.onChange(v || null)}
          error={error}
          disabled={pending || countriesLoading}
        />
      )}
    </UzFormField>
    <UzFormField name="logoUrl">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          // Empty maps to null so the optional URL field validates when blank
          // (the schema rejects '' against the url() rule).
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Logo URL"
          placeholder="https://…"
          hint="Optional. Direct link to the airline logo image."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isLowCost">
      {({ field }) => (
        <UzSwitch
          label="Low-cost carrier"
          description="Mark this airline as a low-cost carrier for display and filtering."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive airlines are hidden from flight components and packages."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const AirlineFormDrawer = ({
  open,
  onOpenChange,
  mode,
  airline,
}: AirlineFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createAirline = useCreateAirline();
  const updateAirline = useUpdateAirline();
  const countriesQuery = useCountryOptions();
  const pending = createAirline.isPending || updateAirline.isPending;

  const countrySelectOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  const createForm = useForm<CreateAirlineRequest>({
    resolver: zodResolver(CreateAirlineRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateAirlineRequest>({
    resolver: zodResolver(UpdateAirlineRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (airline) {
      updateForm.reset({
        iataCode: airline.iataCode,
        icaoCode: airline.icaoCode ?? null,
        name: airline.name,
        countryId: airline.countryId ?? null,
        logoUrl: airline.logoUrl ?? null,
        isLowCost: airline.isLowCost,
        isActive: airline.isActive,
        sortOrder: airline.sortOrder,
      });
    }
  }, [open, isCreate, airline, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createAirline.mutateAsync({
      ...values,
      icaoCode: values.icaoCode?.trim() ? values.icaoCode : null,
      logoUrl: values.logoUrl?.trim() ? values.logoUrl : null,
      countryId: values.countryId ?? null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!airline) return;
    await updateAirline.mutateAsync({
      id: airline.id,
      payload: {
        ...values,
        icaoCode: values.icaoCode?.trim() ? values.icaoCode : null,
        logoUrl: values.logoUrl?.trim() ? values.logoUrl : null,
        countryId: values.countryId ?? null,
      },
    });
    onOpenChange(false);
  });

  const fields = (
    <AirlineFields
      pending={pending}
      countrySelectOptions={countrySelectOptions}
      countriesLoading={countriesQuery.isLoading}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add airline' : `Edit ${airline?.name ?? 'airline'}`}
      description="IATA code is the stable machine identifier (2-3 alphanumerics). ICAO code is optional but also unique."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
